import { useState } from 'react';
import { useCart } from '../context/CartContext';
import {
  Trash2,
  Truck,
  Info,
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  Phone,
  MessageCircle,
  ClipboardList,
  CalendarCheck,
  Layers
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL, getProductImage } from '../services/productCatalog';

export default function Cart() {
  const { cartItems, removeFromCart, updateItem, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [step, setStep] = useState(1); // 1: Cart, 2: Checkout, 3: Success
  const [loading, setLoading] = useState(false);
  const [submittedForm, setSubmittedForm] = useState(null);
  const navigate = useNavigate();

  // Checkout Form with Auto-fill
  const [formData, setFormData] = useState(() => {
    const savedInfo = localStorage.getItem('sbb_delivery_info');
    const parsed = savedInfo ? JSON.parse(savedInfo) : {};
    
    return {
      name: user?.name || parsed.name || '',
      phone: user?.phone || parsed.phone || '',
      email: user?.email || parsed.email || '',
      projectName: parsed.projectName || '',
      projectAddress: parsed.projectAddress || '',
      pouringDate: '',
      pouringTime: '',
      pumpRequired: parsed.pumpRequired || false,
      notes: ''
    };
  });

  const prepareCheckout = () => {
    if (user) {
      setFormData((current) => ({
        ...current,
        name: user.name || current.name,
        email: user.email || current.email,
        phone: user.phone || current.phone,
      }));
    }
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    const updatedForm = { ...formData, [name]: newVal };
    setFormData(updatedForm);
    
    // Save to localStorage for next time (except sensitive or temporary dates)
    if (name !== 'pouringDate' && name !== 'pouringTime') {
      localStorage.setItem('sbb_delivery_info', JSON.stringify(updatedForm));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) {
      console.warn('handleSubmit called while loading, skipping...');
      return;
    }
    
    if (!user) {
      alert(t('cart.loginAlert') || 'Silakan login terlebih dahulu untuk mengirim pesanan.');
      navigate('/login');
      return;
    }

    console.log('Submitting order for user:', user.id);
    setLoading(true);
    try {
      const token = localStorage.getItem('sbb_token');
      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          items: cartItems, 
          customer: formData,
          total_amount: cartTotal
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || (lang === 'id' ? 'Gagal membuat pesanan' : 'Failed to create order'));
      }
      
      const data = await response.json();
      console.log('Order created successfully:', data);
      
      setSubmittedForm({ ...formData });
      setStep(3);
      clearCart();
    } catch (err) {
      console.error('Order submission error:', err);
      alert('Gagal mengirim permintaan: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) {
    return <SuccessView form={submittedForm} />;
  }

  return (
    <div className="cart-page">
      <div className="wrap">
        <header className="cart-header">
          <Link to="/produk-layanan" className="back-link"><ArrowLeft size={16} /> {t('cart.back')}</Link>
          <h1 className="h1">{step === 1 ? t('cart.title1') : t('cart.title2')}</h1>
          <div className="cart-steps">
            <div className={`c-step ${step >= 1 ? 'active' : ''}`}>{t('cart.step1')}</div>
            <div className={`c-step ${step >= 2 ? 'active' : ''}`}>{t('cart.step2')}</div>
            <div className="c-step">{t('cart.step3')}</div>
          </div>
        </header>

        {cartItems.length === 0 && step === 1 ? (
          <EmptyCart />
        ) : (
          <div className="cart-grid">
            <div className="cart-main">
              {step === 1 ? (
                <div className="cart-list">
                  {cartItems.map(item => (
                    <CartItem 
                      key={item.cartId} 
                      item={item} 
                      onRemove={() => removeFromCart(item.cartId)} 
                      onUpdate={(vol) => updateItem(item.cartId, { volume: vol })}
                    />
                  ))}
                </div>
              ) : (
                <CheckoutForm 
                  formData={formData} 
                  onChange={handleInputChange} 
                  onSubmit={handleSubmit}
                  loading={loading}
                  back={() => setStep(1)}
                />
              )}
            </div>

            <aside className="cart-summary">
              <div className="summary-card">
                <h3>{t('cart.summary')}</h3>
                <div className="summary-rows">
                  <div className="s-row">
                    <span>{t('cart.subtotal')}</span>
                    <span>Rp {cartTotal.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}</span>
                  </div>
                  <div className="s-row">
                    <span>{t('cart.delivery')}</span>
                    <span className="info-txt">{t('cart.calcLater')}</span>
                  </div>
                  <div className="s-row total">
                    <span>{t('cart.estTotal')}</span>
                    <span>Rp {cartTotal.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}</span>
                  </div>
                </div>
                
                {step === 1 && (
                  <button 
                    className="btn btn-red w-full" 
                    onClick={prepareCheckout}
                  >
                    {t('cart.next')} <ChevronRight size={18} />
                  </button>
                )}
                
                <div className="summary-note">
                  <Info size={14} />
                  <p>{t('cart.note')}</p>
                </div>
              </div>

              <div className="trust-box">
                <div className="t-item"><CheckCircle2 size={16} /> {t('cart.trust1')}</div>
                <div className="t-item"><CheckCircle2 size={16} /> {t('cart.trust2')}</div>
                <div className="t-item"><CheckCircle2 size={16} /> {t('cart.trust3')}</div>
              </div>
            </aside>
          </div>
        )}
      </div>
      <style>{`
        .cart-page { min-height: 70vh; padding: 64px 0 90px; background: #f5f5f1; }
        .cart-page .wrap { width: min(1180px, calc(100% - 48px)); margin: 0 auto; }
        .cart-header { margin-bottom: 36px; }
        .back-link { display: inline-flex; align-items: center; gap: 7px; margin-bottom: 18px; color: #777654; font-size: 12px; font-weight: 700; }
        .cart-header .h1 { margin-bottom: 24px; color: #242424; font-size: clamp(30px, 4vw, 46px); }
        .cart-steps { display: flex; max-width: 530px; }
        .c-step { flex: 1; padding: 10px 8px; border-bottom: 3px solid #d8d8d2; color: #8a8a8a; font-size: 11px; font-weight: 700; text-align: center; }
        .c-step.active { border-color: #777654; color: #777654; }
        .cart-grid { display: grid; grid-template-columns: minmax(0, 1fr) 350px; gap: 28px; align-items: start; }
        .cart-list { display: flex; flex-direction: column; gap: 14px; }
        .cart-item { display: grid; grid-template-columns: 110px minmax(0, 1fr) 90px 150px 34px; gap: 18px; align-items: center; padding: 18px; background: #fff; border: 1px solid #e1e1dc; }
        .ci-img img { width: 110px; height: 92px; object-fit: cover; }
        .ci-cat { margin-bottom: 5px; color: #777654; font-size: 9px; font-weight: 800; text-transform: uppercase; }
        .ci-name { margin-bottom: 8px; font-size: 15px; }
        .ci-truck { display: flex; align-items: center; gap: 5px; color: #777; font-size: 10px; }
        .ci-qty label { display: block; margin-bottom: 5px; color: #777; font-size: 9px; }
        .ci-qty input { width: 82px; height: 40px; padding: 0 9px; border: 1px solid #d8d8d2; }
        .ci-price { text-align: right; }
        .p-unit { margin-bottom: 5px; color: #777; font-size: 9px; }
        .p-sub { color: #242424; font-size: 14px; font-weight: 800; }
        .ci-remove { color: #b42318; }
        .summary-card,
        .trust-box,
        .checkout-form { background: #fff; border: 1px solid #e1e1dc; }
        .summary-card { padding: 26px; }
        .summary-card h3 { margin-bottom: 20px; font-size: 18px; }
        .summary-rows { margin-bottom: 22px; }
        .s-row { display: flex; justify-content: space-between; gap: 18px; padding: 10px 0; color: #666; font-size: 11px; }
        .s-row.total { margin-top: 8px; padding-top: 16px; border-top: 1px solid #deded8; color: #242424; font-size: 14px; font-weight: 800; }
        .info-txt { text-align: right; color: #777654; }
        .w-full { width: 100%; }
        .btn-red { background: #777654; color: #fff; border-color: #777654; }
        .btn-red:hover { background: #65644a; color: #fff; }
        .btn-outline { border: 1px solid #777654; color: #777654; background: #fff; }
        .summary-note { display: flex; align-items: flex-start; gap: 7px; margin-top: 18px; color: #777; font-size: 9px; line-height: 1.5; }
        .trust-box { margin-top: 14px; padding: 18px 22px; }
        .t-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; color: #555; font-size: 10px; }
        .t-item svg { color: #777654; }
        .checkout-form { padding: 28px; }
        .form-section { margin-bottom: 30px; }
        .form-section h3 { margin-bottom: 18px; padding-bottom: 10px; border-bottom: 1px solid #e4e4df; font-size: 17px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .fg-full { grid-column: 1 / -1; }
        .fg label,
        .fg-full label { display: block; margin-bottom: 7px; color: #555; font-size: 11px; font-weight: 700; }
        .fg input,
        .fg-full input,
        .fg-full textarea { width: 100%; padding: 12px; border: 1px solid #d8d8d2; font: inherit; font-size: 12px; }
        .fg-full textarea { min-height: 100px; resize: vertical; }
        .check-group { display: flex; align-items: flex-start; gap: 10px; }
        .check-group label { display: flex; flex-direction: column; gap: 4px; font-size: 11px; }
        .check-group span { color: #777; }
        .checkout-actions { display: flex; justify-content: space-between; gap: 12px; }
        .empty-cart { padding: 70px 24px; background: #fff; border: 1px solid #e1e1dc; text-align: center; }
        .e-icon { margin-bottom: 18px; color: #777654; }
        .empty-cart h2 { margin-bottom: 8px; }
        .empty-cart p { margin-bottom: 22px; color: #777; }

        /* ── Success Page ── */
        .success-page {
          min-height: 100vh;
          padding: 64px 24px 80px;
          background: #f5f5f1;
        }
        .success-page .wrap { max-width: 760px; margin: 0 auto; }
        .success-hero {
          text-align: center;
          margin-bottom: 36px;
        }
        .success-check-wrap {
          width: 88px; height: 88px;
          border-radius: 50%;
          background: #eaf5d4;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 22px;
          animation: popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        @keyframes popIn {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        .success-hero h1 {
          font-size: 28px; font-weight: 800;
          color: #1a1a1a; margin-bottom: 10px;
          letter-spacing: -0.4px;
        }
        .success-hero p {
          color: #666; font-size: 15px;
          line-height: 1.65; max-width: 500px;
          margin: 0 auto;
        }
        .success-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }
        .success-box {
          background: #fff;
          border: 1px solid #e1e1dc;
          border-radius: 12px;
          padding: 24px;
        }
        .success-box-title {
          display: flex; align-items: center; gap: 9px;
          font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.6px;
          color: #777654; margin-bottom: 16px;
        }
        .success-box-title svg { flex-shrink: 0; }
        .success-detail-row {
          display: flex; flex-direction: column;
          gap: 12px;
        }
        .sdr-item { display: flex; flex-direction: column; gap: 2px; }
        .sdr-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #aaa; }
        .sdr-val { font-size: 14px; font-weight: 600; color: #1a1a1a; }

        /* Timeline */
        .success-timeline { display: flex; flex-direction: column; gap: 0; }
        .tl-item {
          display: flex; gap: 16px;
          position: relative; padding-bottom: 20px;
        }
        .tl-item:last-child { padding-bottom: 0; }
        .tl-left {
          display: flex; flex-direction: column; align-items: center;
          flex-shrink: 0;
        }
        .tl-dot {
          width: 32px; height: 32px;
          border-radius: 50%;
          display: grid; place-items: center;
          background: #f0f0eb;
          color: #777654;
          flex-shrink: 0;
          font-size: 13px; font-weight: 800;
        }
        .tl-dot.done { background: #eaf5d4; color: #5a9c1a; }
        .tl-line {
          width: 2px; background: #e8e8e2;
          flex: 1; margin: 6px 0;
        }
        .tl-content { padding-top: 4px; }
        .tl-content strong { display: block; font-size: 13px; font-weight: 700; color: #1a1a1a; margin-bottom: 2px; }
        .tl-content span { font-size: 12px; color: #888; line-height: 1.55; }
        .tl-badge {
          display: inline-block;
          margin-top: 5px;
          padding: 2px 9px;
          border-radius: 999px;
          font-size: 10px; font-weight: 700;
          background: #fff4e0; color: #b06000;
        }

        /* Actions */
        .success-actions {
          display: flex; justify-content: center;
          gap: 12px; margin-top: 8px;
        }
        .success-trust {
          text-align: center; margin-top: 28px;
          color: #aaa; font-size: 12px;
        }
        @media (max-width: 900px) {
          .cart-grid { grid-template-columns: 1fr; }
          .cart-summary { order: -1; }
          .cart-item { grid-template-columns: 90px 1fr 34px; }
          .ci-img img { width: 90px; height: 80px; }
          .ci-qty { grid-column: 2; }
          .ci-price { grid-column: 2; text-align: left; }
          .ci-remove { grid-column: 3; grid-row: 1; }
        }
        @media (max-width: 560px) {
          .cart-page .wrap { width: min(100% - 28px, 1180px); }
          .form-grid { grid-template-columns: 1fr; }
          .fg-full { grid-column: auto; }
          .checkout-actions { flex-direction: column; }
          .success-body { grid-template-columns: 1fr; }
          .success-actions { flex-direction: column; }
        }
      `}</style>
    </div>
  );
}

function CartItem({ item, onRemove, onUpdate }) {
  const { t, lang } = useLanguage();
  const img = getProductImage(item);

  return (
    <div className="cart-item">
      <div className="ci-img">
        <img src={img} alt={item.name} />
      </div>
      <div className="ci-info">
        <div className="ci-cat">{item.category}</div>
        <h4 className="ci-name">{item.name}</h4>
        <div className="ci-meta">
          <div className="ci-truck"><Truck size={14} /> {item.truckType === 'standard' ? (lang === 'id' ? 'Truck Standard (7m³)' : 'Standard Truck (7m³)') : 'MiniMix (3m³)'}</div>
        </div>
      </div>
      <div className="ci-qty">
        <label>{t('cart.item.volume')}</label>
        <input 
          type="number" 
          value={item.volume} 
          step="0.1" 
          min="1"
          onChange={(e) => onUpdate(parseFloat(e.target.value))}
        />
      </div>
      <div className="ci-price">
        <div className="p-unit">Rp {item.pricePerM3.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')} {t('cart.item.unit')}</div>
        <div className="p-sub">Rp {(item.volume * item.pricePerM3).toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}</div>
      </div>
      <button className="ci-remove" onClick={onRemove}><Trash2 size={18} /></button>
    </div>
  );
}

function CheckoutForm({ formData, onChange, onSubmit, loading, back }) {
  const { t } = useLanguage();
  return (
    <form className="checkout-form" onSubmit={onSubmit}>
      <div className="form-section">
        <h3>{t('cart.form.contact')}</h3>
        <div className="form-grid">
          <div className="fg">
            <label>{t('cart.form.name')}</label>
            <input type="text" name="name" value={formData.name} onChange={onChange} required placeholder="Contoh: Budi Santoso" />
          </div>
          <div className="fg">
            <label>{t('cart.form.phone')}</label>
            <input type="tel" name="phone" value={formData.phone} onChange={onChange} required placeholder="0812..." />
          </div>
          <div className="fg-full">
            <label>{t('cart.form.email')}</label>
            <input type="email" name="email" value={formData.email} onChange={onChange} required placeholder="budi@example.com" />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('cart.form.project')}</h3>
        <div className="form-grid">
          <div className="fg-full">
            <label>{t('cart.form.projectName')}</label>
            <input type="text" name="projectName" value={formData.projectName} onChange={onChange} required placeholder="Contoh: Dak Rumah Lantai 2" />
          </div>
          <div className="fg-full">
            <label>{t('cart.form.address')}</label>
            <textarea name="projectAddress" value={formData.projectAddress} onChange={onChange} required placeholder="Jl. Raya No. 123..."></textarea>
          </div>
          <div className="fg">
            <label>{t('cart.form.date')}</label>
            <input type="date" name="pouringDate" value={formData.pouringDate} onChange={onChange} required />
          </div>
          <div className="fg">
            <label>{t('cart.form.time')}</label>
            <input type="time" name="pouringTime" value={formData.pouringTime} onChange={onChange} required />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('cart.form.options')}</h3>
        <div className="check-group">
          <input type="checkbox" id="pump" name="pumpRequired" checked={formData.pumpRequired} onChange={onChange} />
          <label htmlFor="pump">
            <strong>{t('cart.form.pump')}</strong>
            <span>{t('cart.form.pumpSub')}</span>
          </label>
        </div>
      </div>

      <div className="checkout-actions">
        <button type="button" className="btn btn-outline" onClick={back}>{t('cart.form.back')}</button>
        <button type="submit" className="btn btn-red" disabled={loading}>
          {loading ? t('cart.form.sending') : t('cart.form.submit')} 
          <ArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
        </button>
      </div>
    </form>
  );
}

function EmptyCart() {
  const { t } = useLanguage();
  return (
    <div className="empty-cart">
      <div className="e-icon"><ShoppingCartIcon size={48} /></div>
      <h2>{t('cart.emptyTitle')}</h2>
      <p>{t('cart.emptySub')}</p>
      <Link to="/produk-layanan" className="btn btn-red">{t('cart.viewCatalog')}</Link>
    </div>
  );
}

function SuccessView({ form }) {
  const { t, lang } = useLanguage();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const timeline = [
    {
      icon: <Phone size={15} />,
      title: lang === 'id' ? 'Tim Sales Menghubungi Anda' : 'Sales Team Will Contact You',
      sub: lang === 'id' ? 'Via WhatsApp atau telepon dalam 30 menit kerja.' : 'Via WhatsApp or call within 30 business minutes.',
      badge: lang === 'id' ? '~30 menit' : '~30 min',
      done: true,
    },
    {
      icon: <ClipboardList size={15} />,
      title: lang === 'id' ? 'Konfirmasi & Quotation' : 'Confirmation & Quotation',
      sub: lang === 'id' ? 'Sales memverifikasi detail lokasi dan memberikan penawaran harga resmi.' : 'Sales verifies location details and sends official quotation.',
      badge: null,
      done: false,
    },
    {
      icon: <Truck size={15} />,
      title: lang === 'id' ? 'Pengiriman Beton ke Lokasi' : 'Concrete Delivery On-Site',
      sub: lang === 'id' ? 'Armada dikirim sesuai jadwal pengecoran yang disepakati.' : 'Fleet dispatched per agreed pouring schedule.',
      badge: null,
      done: false,
    },
  ];

  return (
    <div className="success-page">
      <div className="wrap">

        {/* Hero */}
        <div className="success-hero">
          <div className="success-check-wrap">
            <CheckCircle2 size={44} color="#5a9c1a" strokeWidth={2} />
          </div>
          <h1>{t('cart.success.title')}</h1>
          <p>{t('cart.success.desc')}</p>
        </div>

        {/* Two-column: detail + timeline */}
        <div className="success-body">

          {/* Detail Pesanan */}
          <div className="success-box">
            <div className="success-box-title"><Layers size={14} />{lang === 'id' ? 'Detail Permintaan' : 'Request Details'}</div>
            <div className="success-detail-row">
              {form?.name && (
                <div className="sdr-item">
                  <span className="sdr-label">{lang === 'id' ? 'Nama' : 'Name'}</span>
                  <span className="sdr-val">{form.name}</span>
                </div>
              )}
              {form?.phone && (
                <div className="sdr-item">
                  <span className="sdr-label">WhatsApp</span>
                  <span className="sdr-val">{form.phone}</span>
                </div>
              )}
              {form?.projectName && (
                <div className="sdr-item">
                  <span className="sdr-label">{lang === 'id' ? 'Nama Proyek' : 'Project Name'}</span>
                  <span className="sdr-val">{form.projectName}</span>
                </div>
              )}
              {form?.pouringDate && (
                <div className="sdr-item">
                  <span className="sdr-label">{lang === 'id' ? 'Rencana Cor' : 'Pour Date'}</span>
                  <span className="sdr-val">{formatDate(form.pouringDate)}{form.pouringTime ? ` · ${form.pouringTime}` : ''}</span>
                </div>
              )}
              {form?.projectAddress && (
                <div className="sdr-item">
                  <span className="sdr-label">{lang === 'id' ? 'Alamat Proyek' : 'Project Address'}</span>
                  <span className="sdr-val" style={{ whiteSpace: 'pre-line' }}>{form.projectAddress}</span>
                </div>
              )}
            </div>
          </div>

          {/* Timeline selanjutnya */}
          <div className="success-box">
            <div className="success-box-title"><CalendarCheck size={14} />{lang === 'id' ? 'Langkah Selanjutnya' : 'What Happens Next'}</div>
            <div className="success-timeline">
              {timeline.map((item, i) => (
                <div className="tl-item" key={i}>
                  <div className="tl-left">
                    <div className={`tl-dot${item.done ? ' done' : ''}`}>{item.icon}</div>
                    {i < timeline.length - 1 && <div className="tl-line" />}
                  </div>
                  <div className="tl-content">
                    <strong>{item.title}</strong>
                    <span>{item.sub}</span>
                    {item.badge && <div className="tl-badge">{item.badge}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="success-actions">
          <Link to="/portal" className="btn btn-red">
            <ClipboardList size={17} /> {t('cart.success.checkOrder')}
          </Link>
          <a
            href={`https://wa.me/6282110005600?text=${encodeURIComponent(
              `Halo, saya ${form?.name || ''} ingin konfirmasi permintaan beton untuk proyek ${form?.projectName || ''}.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
          >
            <MessageCircle size={17} /> {t('cart.success.whatsapp')}
          </a>
        </div>

        <div className="success-trust">
          🔒 {lang === 'id' ? 'Data Anda aman & terenkripsi · SBB member of SIG' : 'Your data is safe & encrypted · SBB member of SIG'}
        </div>

      </div>
    </div>
  );
}

function ShoppingCartIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
  );
}
